/**
 * PriceCheck.jsx — v1-parity item search with rich result cards, inline
 * source/sort controls, ESO-Hub PC-NA server metrics, and Fissal price tiers.
 *
 * Settings:
 *   q       — initial query (text)
 *   mode    — display filter: 'all' | 'mm' | 'ttc' | 'esohub' | 'ghost'
 *   sort    — 'relevance' | 'recent' | 'price-desc' | 'price-asc' | 'name'
 */

import { createSignal, Show, For, createMemo, createEffect, onCleanup, onMount } from 'solid-js';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import {
  gold, qty, relTime, freqLabel,
  qualityColor, esoIconUrl, ESO_ICON_FALLBACK,
} from '../lib/format.js';
import ItemSearchInput from '../components/ItemSearchInput.jsx';
import { formatFissalSource } from '../components/FissalSuggestion.jsx';
import ItemDetail from './ItemDetail.jsx';
import './PriceCheck.css';

// Issue #114 — module-scoped cache so we don't re-hit /api/items/:name/fissal
// for items the user has already scrolled past in this session.
const FISSAL_CACHE = new Map();

const MODE_OPTIONS = [
  ['all',    'All'],
  ['mm',     'MM Guild'],
  ['ttc',    'TTC'],
  ['esohub', 'ESO-Hub'],
  ['ghost',  'Ghost'],
];

const SORT_OPTIONS = [
  ['relevance',  'Relevance'],
  ['recent',     'Recent'],
  ['price-desc', 'Price ↓'],
  ['price-asc',  'Price ↑'],
  ['name',       'Name A→Z'],
];

const QUICK_PICKS = [
  'Dreugh Wax',
  'Chromium Plating',
  'Perfect Roe',
  'Aetherial Dust',
  'Columbine',
  'Heartwood',
];

const QUALITY_OPTIONS = [
  ['', 'Any quality'], ['white', 'White'], ['green', 'Green'],
  ['blue', 'Blue'], ['purple', 'Purple'], ['gold', 'Gold'],
];
// Legacy parity: keep trait filters grouped by equipment type so the
// chooser mirrors the v1 mental model (weapon/armor/jewelry/universal).
const TRAIT_GROUPS = [
  {
    label: 'Weapon Traits',
    options: [
      ['charged', 'Charged'], ['decisive', 'Decisive'], ['infused', 'Infused'],
      ['nirnhoned', 'Nirnhoned'], ['powered', 'Powered'], ['precise', 'Precise'],
      ['sharpened', 'Sharpened'], ['training', 'Training'],
    ],
  },
  {
    label: 'Armor Traits',
    options: [
      ['defending', 'Defending'], ['divines', 'Divines'], ['impenetrable', 'Impenetrable'],
      ['invigorating', 'Invigorating'], ['reinforced', 'Reinforced'], ['sturdy', 'Sturdy'],
      ['well-fitted', 'Well-Fitted'],
    ],
  },
  {
    label: 'Jewelry Traits',
    options: [
      ['arcane', 'Arcane'], ['bloodthirsty', 'Bloodthirsty'], ['harmony', 'Harmony'],
      ['healthy', 'Healthy'], ['protective', 'Protective'], ['robust', 'Robust'],
      ['swift', 'Swift'], ['triune', 'Triune'],
    ],
  },
  {
    label: 'Universal',
    options: [['intricate', 'Intricate'], ['ornate', 'Ornate']],
  },
];
const TRAIT_OPTIONS = [['', 'Any trait'], ...TRAIT_GROUPS.flatMap((group) => group.options)];
const optionLabel = (options, value) => options.find(([v]) => v === value)?.[1] || value;

export default function PriceCheck(props) {
  const isItemDetailType = () => props.widget?.type === 'item-detail' || props.widget?.type === 'item-analysis';
  const initialAnalysisName = String(props.settings?.name || props.settings?.item || '').trim();
  const [activeTab, setActiveTab] = createSignal(
    (isItemDetailType() || initialAnalysisName || props.settings?.tab === 'analysis') ? 'analysis' : 'search'
  );
  const [selectedItem, setSelectedItem] = createSignal(initialAnalysisName);
  createEffect(() => {
    const extName = String(props.settings?.name || props.settings?.item || '').trim();
    if (extName) {
      setSelectedItem(extName);
      if (isItemDetailType()) setActiveTab('analysis');
    }
  });
  const [q, setQ] = createSignal(props.settings?.q || '');
  const [qDebounced, setQDebounced] = createSignal(q());
  const [filtersOpen, setFiltersOpen] = createSignal(Boolean(props.settings?.quality || props.settings?.trait));
  const [quality, setQuality] = createSignal(props.settings?.quality || '');
  const [trait, setTrait] = createSignal(props.settings?.trait || '');
  const [mode, setMode] = createSignal(props.settings?.mode || 'all');
  const [sort, setSort] = createSignal(props.settings?.sort || 'relevance');

  createEffect(() => {
    setQuality(props.settings?.quality || '');
    setTrait(props.settings?.trait || '');
    setMode(props.settings?.mode || 'all');
    setSort(props.settings?.sort || 'relevance');
  });

  // Backend price-check schema accepts 'all' | 'mm' | 'ttc' | 'ghost'
  const backendMode = createMemo(() => (mode() === 'esohub' ? 'all' : mode()));

  const params = createMemo(() => ({
    q: qDebounced(),
    mode: backendMode(),
    sort: sort(),
    quality: quality() || null,
    trait: trait() || null,
    pageSize: 50,
  }));
  const { data, loading, error } = useWidgetData('price-check', params);

  // Client-side mode filter (defence-in-depth; backend also respects it).
  const filtered = createMemo(() => {
    const all = data()?.items || [];
    const m = mode();
    if (m === 'mm')     return all.filter(it => (it.mmAvgPrice > 0 || it.totalSales > 0));
    if (m === 'ttc')    return all.filter(it => (it.ttcAvgPrice > 0 || it.suggestedPrice > 0 || it.saleAvg > 0));
    if (m === 'esohub') return all.filter(it => (it.esoHubSaleAvg > 0 || it.esoHubListAvg > 0 || it.esoHubSales > 0));
    if (m === 'ghost')  return all.filter(it => it.noSalesData || it.ttcOnly);
    return all;
  });
  const total = () => data()?.total || 0;
  const activeFilters = createMemo(() => {
    const chips = [];
    if (quality()) chips.push({ key: 'quality', label: `Quality: ${optionLabel(QUALITY_OPTIONS, quality())}` });
    if (trait()) chips.push({ key: 'trait', label: `Trait: ${optionLabel(TRAIT_OPTIONS, trait())}` });
    return chips;
  });

  function persistSetting(key, value) {
    if (key === 'quality') setQuality(value);
    if (key === 'trait')   setTrait(value);
    if (key === 'mode')    setMode(value || 'all');
    if (key === 'sort')    setSort(value || 'relevance');
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), [key]: value || null });
    } catch { /* settings persistence is best-effort */ }
  }

  function persistQuery(nextQ) {
    setQDebounced(nextQ);
    if (!props.widget?.id) return;
    const prevQ = props.widget?.settings?.q || '';
    if ((nextQ || '') === prevQ) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), q: nextQ || null });
    } catch { /* best-effort */ }
  }

  function selectQuickPick(itemName) {
    setQ(itemName);
    persistQuery(itemName);
  }

  function clearFilter(key) { persistSetting(key, ''); }

  function clearAllFilters() {
    setQuality('');
    setTrait('');
    if (!props.widget?.id) return;
    try {
      wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), quality: null, trait: null });
    } catch { /* settings persistence is best-effort */ }
  }

  function pickItem(name) {
    if (!name) return;
    setSelectedItem(name);
    setActiveTab('analysis');
    if (props.widget?.id) {
      try { wm.setSettings(props.widget.id, { ...(props.widget.settings || {}), name }); } catch {}
    }
  }

  function popoutItemModal(name, icon) {
    if (!name) return;
    window.dispatchEvent(new CustomEvent('v2-item-pick', { detail: { name, icon } }));
  }

  return (
    <div class="v2-widget v2-pc">
      <div class="v2-tabs" role="tablist" aria-label="Price Check & Item Analysis" style={{ 'margin-bottom': '6px' }}>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab() === 'search'}
          class={`v2-tab ${activeTab() === 'search' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Search & Browse
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab() === 'analysis'}
          class={`v2-tab ${activeTab() === 'analysis' ? 'is-active' : ''}`}
          onClick={() => setActiveTab('analysis')}
        >
          📊 Item Analysis{selectedItem() ? ` · ${selectedItem().slice(0, 22)}` : ''}
        </button>
        <Show when={activeTab() === 'analysis' && selectedItem()}>
          <button
            type="button"
            class="v2-btn-sm"
            style={{ 'margin-left': 'auto' }}
            onClick={() => popoutItemModal(selectedItem())}
            title="Open item in floating modal"
          >
            ⤢ Modal
          </button>
        </Show>
      </div>
      <Show when={activeTab() === 'analysis'}>
        <div style={{ display: 'flex', 'flex-direction': 'column', flex: 1, 'min-height': 0 }}>
          <ItemDetail
            widget={props.widget}
            settings={{ ...(props.settings || {}), name: selectedItem() || q() }}
            identity={props.identity}
          />
        </div>
      </Show>
      <Show when={activeTab() === 'search'}>
        <div class="v2-pc__search-row">
          <ItemSearchInput
            class="v2-pc__searchbar"
            inputClass="v2-pc__input"
            initialValue={q()}
            placeholder="Search items… (e.g. Perfect Roe)"
            onValue={(rawVal, debounced) => {
              setQ(rawVal);
              if (debounced !== undefined) persistQuery(debounced);
            }}
            onSubmit={(v) => {
              persistQuery(v);
            }}
          />
          <button
            class={`v2-btn-sm v2-pc__filter-toggle ${filtersOpen() ? 'is-open' : ''}`}
            type="button"
            aria-expanded={filtersOpen() ? 'true' : 'false'}
            onClick={() => setFiltersOpen(!filtersOpen())}
            title="Show ESO item filters"
          >
            Filters<Show when={activeFilters().length}> ({activeFilters().length})</Show>
          </button>
        </div>

        {/* Inline source mode pills + sort selector */}
        <div class="v2-pc__toolbar" role="toolbar" aria-label="Source filter and sort order">
          <div class="v2-pc__mode-pills" role="group" aria-label="Price source">
            <For each={MODE_OPTIONS}>{([val, label]) => (
              <button
                type="button"
                class={`v2-pc__mode-pill ${mode() === val ? 'is-active' : ''}`}
                onClick={() => persistSetting('mode', val)}
                title={`Filter by source: ${label}`}
              >
                {label}
              </button>
            )}</For>
          </div>
          <label class="v2-pc__sort-wrap" title="Sort results">
            <span class="v2-pc__sort-label">Sort</span>
            <select
              class="v2-pc__sort-select"
              value={sort()}
              onChange={(e) => persistSetting('sort', e.currentTarget.value)}
            >
              <For each={SORT_OPTIONS}>{([val, label]) => (
                <option value={val}>{label}</option>
              )}</For>
            </select>
          </label>
        </div>

        <Show when={filtersOpen()}>
          <div class="v2-pc__filter-panel">
            <label class="v2-pc__filter-field">
              <span>Quality</span>
              <select value={quality()} onChange={(e) => persistSetting('quality', e.currentTarget.value)}>
                <For each={QUALITY_OPTIONS}>{([value, label]) => <option value={value}>{label}</option>}</For>
              </select>
            </label>
            <label class="v2-pc__filter-field">
              <span>Trait</span>
              <select value={trait()} onChange={(e) => persistSetting('trait', e.currentTarget.value)}>
                <option value="">Any trait</option>
                <For each={TRAIT_GROUPS}>{(group) => (
                  <optgroup label={group.label}>
                    <For each={group.options}>{([value, label]) => <option value={value}>{label}</option>}</For>
                  </optgroup>
                )}</For>
              </select>
            </label>
          </div>
        </Show>

        <Show when={activeFilters().length}>
          <div class="v2-pc__active-filters" aria-label="Active Price Check filters">
            <For each={activeFilters()}>{(chip) => (
              <button class="v2-pc__filter-chip" type="button" onClick={() => clearFilter(chip.key)} title={`Clear ${chip.label}`}>
                {chip.label} ×
              </button>
            )}</For>
            <button class="v2-pc__filter-clear" type="button" onClick={clearAllFilters}>Clear all</button>
          </div>
        </Show>

        <Show when={qDebounced()} fallback={
          <div class="v2-empty v2-pc__hint" style={{ 'text-align': 'center', padding: 'var(--sp-3)' }}>
            <div style={{ 'font-size': 'var(--fs-md)', 'margin-bottom': 'var(--sp-2)', color: 'var(--fissal)' }}>
              🏷️ Price Check & Market Intelligence
            </div>
            <div style={{ color: 'var(--t-green-dim)', 'margin-bottom': 'var(--sp-2)' }}>
              Search any ESO item above to compare <b>Redfur MM</b>, <b>TTC</b>, and <b>ESO-Hub PC-NA</b> confirmed sales alongside Fissal's Quick Sell / Patient List pricing tiers.
            </div>
            <div class="v2-pc__quick-picks" aria-label="Popular item searches">
              <span class="v2-pc__quick-label">Quick check:</span>
              <For each={QUICK_PICKS}>{(itemName) => (
                <button
                  type="button"
                  class="v2-pc__quick-chip"
                  onClick={() => selectQuickPick(itemName)}
                >
                  {itemName}
                </button>
              )}</For>
            </div>
          </div>
        }>
          <Show when={!error()} fallback={<div class="v2-error">{error()}</div>}>
            <Show when={!loading() || filtered().length} fallback={<div class="v2-widget-skel">Searching…</div>}>
              <Show when={filtered().length} fallback={<div class="v2-empty">No matches.</div>}>
                <div class="v2-pc__count">
                  {qty(filtered().length)} of {qty(total())} match
                  <Show when={mode() !== 'all'}>
                    <span class="v2-pc__mode-tag"> · {mode()}</span>
                  </Show>
                  <Show when={loading()}>
                    <span class="v2-dim" style={{ 'margin-left': '6px', 'font-size': 'var(--fs-xs)' }}>· refreshing…</span>
                  </Show>
                </div>
                <div class="v2-widget__scroll v2-pc__list" style={{ 'opacity': loading() ? 0.6 : 1, transition: 'opacity 120ms' }}>
                  <For each={filtered()}>{(it) => (
                    <PriceRow item={it} onPick={pickItem} onPopout={popoutItemModal} />
                  )}</For>
                </div>
              </Show>
            </Show>
          </Show>
        </Show>
      </Show>
    </div>
  );
}

function PriceRow(props) {
  const it = props.item;
  const qColor = qualityColor(it.quality);
  const iconSrc = it.iconUrl || esoIconUrl(it.icon, it.name);
  const hasMM  = it.mmAvgPrice  > 0 || it.totalSales > 0;
  const hasTTC = it.ttcAvgPrice > 0 || it.suggestedPrice > 0 || it.saleAvg > 0;
  const hasEH  = it.esoHubSaleAvg > 0 || it.esoHubListAvg > 0 || it.esoHubSales > 0;
  const ghost  = it.noSalesData ? 'v2-pc-row--ghost' : (it.ttcOnly ? 'v2-pc-row--ttconly' : '');

  const mmHead = it.mmAvgPrice > 0
    ? { value: gold(it.mmAvgPrice), note: null }
    : (it.mmLowPrice > 0 && it.mmHighPrice > 0
        ? { value: `${gold(it.mmLowPrice)}–${gold(it.mmHighPrice)}`, note: 'range' }
        : null);
  const mmHasRange = it.mmLowPrice > 0 && it.mmHighPrice > 0
    && (it.mmLowPrice !== it.mmHighPrice);

  const ttcHead = it.suggestedPrice > 0
    ? { value: gold(it.suggestedPrice), note: null }
    : it.saleAvg > 0
      ? { value: gold(it.saleAvg), note: 'sold avg' }
      : it.ttcAvgPrice > 0
        ? { value: gold(it.ttcAvgPrice), note: 'listed avg' }
        : null;

  return (
    <div class={`v2-pc-row v2-pick--item ${ghost}`} data-icon={iconSrc}
      data-pick-key={it.name || ''}
      tabindex="0"
      role="button"
      aria-label={`Open item analysis for ${it.name}`}
      onClick={() => props.onPick(it.name, { icon: iconSrc })}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return;
        e.preventDefault();
        props.onPick(it.name, { icon: iconSrc });
      }}>
      <div class="v2-pc-row__head">
        <img class="v2-pc-row__icon" src={iconSrc} alt="" loading="lazy"
          onError={(e) => {
            const img = e.currentTarget;
            if (img.src.endsWith('.png')) img.src = img.src.replace('.png', '.jpg');
            else { img.onerror = null; img.src = ESO_ICON_FALLBACK; }
          }} />
        <Show when={it.quality}>
          <span class="v2-quality-dot" style={{ background: qColor }} title={it.quality} />
        </Show>
        <span class="v2-pc-row__name" style={{ color: qColor }}>{it.name}</span>
        <Show when={it.trait}>
          <span class="v2-trait-badge">{it.trait}</span>
        </Show>
        <Show when={it.noSalesData}>
          <span class="v2-pc-row__ghost-tag">No sales recorded</span>
        </Show>
        <Show when={!it.noSalesData && it.ttcOnly}>
          <span class="v2-pc-row__ghost-tag v2-pc-row__ghost-tag--ttc">TTC only</span>
        </Show>
        <button
          type="button"
          class="v2-pc-row__popout"
          title="Pop out item in floating modal"
          aria-label={`Pop out ${it.name} in modal`}
          onClick={(e) => {
            e.stopPropagation();
            props.onPopout?.(it.name, iconSrc);
          }}
        >
          ⤢
        </button>
      </div>

      <div class="v2-pc-row__prices">
        <div class={`v2-pc-price v2-pc-price--mm ${hasMM ? '' : 'v2-pc-price--empty'}`}>
          <span class="v2-pc-price__tag" title="Fissal Relay — guild sales recorded by Redfur addon users.">MM</span>
          <span class="v2-pc-price__main"
            title="Fissal Relay avg.Mean — mean per-unit price across recorded guild sales.">
            {mmHead ? mmHead.value : <span class="v2-dim">—</span>}
            <Show when={mmHead && !mmHead.note}>
              <span class="v2-pc-price__label" title="avg.Mean = mean per-unit Fissal Relay sale price.">avg.Mean</span>
            </Show>
            <Show when={mmHead?.note === 'range'}>
              <span class="v2-pc-price__fallback-note" title="No mean available; showing low–high range instead.">range</span>
            </Show>
          </span>
          <span class="v2-pc-price__sub">
            <Show when={mmHasRange}>
              <span class="v2-pc-price__range" title="avg.Low – avg.High: lowest and highest per-unit Fissal Relay sale prices observed.">
                <span class="v2-pc-price__label v2-pc-price__label--sub">avg.Low</span> {gold(it.mmLowPrice)}
                <span class="v2-pc-price__sep"> – </span>
                <span class="v2-pc-price__label v2-pc-price__label--sub">avg.High</span> {gold(it.mmHighPrice)}
              </span>
              <span class="v2-pc-price__sep"> · </span>
            </Show>
            <span title="Recorded Fissal Relay sales count and total units sold.">
              {qty(it.totalSales || 0)} sales<Show when={it.totalVolume}> · {qty(it.totalVolume)} vol</Show>
            </span>
          </span>
        </div>
        <div class={`v2-pc-price v2-pc-price--ttc ${hasTTC ? '' : 'v2-pc-price--empty'}`}>
          <span class="v2-pc-price__tag" title="Tamriel Trade Centre — listings + sales scraped from the TTC addon.">TTC</span>
          <span class="v2-pc-price__main v2-pc-price__main--suggested"
            title="TTC Suggested — TTC's recommended sale price. Headline figure when available; otherwise the best supporting figure is shown.">
            {ttcHead ? ttcHead.value : <span class="v2-dim">—</span>}
            <Show when={ttcHead && !ttcHead.note}>
              <span class="v2-pc-price__label v2-pc-price__label--suggested" title="This is TTC's recommended sale price.">Suggested</span>
            </Show>
            <Show when={ttcHead?.note}>
              <span class="v2-pc-price__fallback-note" title="No suggested price; showing the best supporting figure available.">
                {ttcHead.note}
              </span>
            </Show>
          </span>
          <span class="v2-pc-price__sub">
            <Show when={it.saleAvg > 0 && ttcHead?.note !== 'sold avg'}>
              <span title="avg.Sold — average price across recently sold TTC listings.">
                avg.Sold: {gold(it.saleAvg)}
              </span>
            </Show>
            <Show when={it.ttcAvgPrice > 0 && ttcHead?.note !== 'listed avg'}>
              <Show when={it.saleAvg > 0 && ttcHead?.note !== 'sold avg'}>
                <span class="v2-pc-price__sep"> · </span>
              </Show>
              <span class="v2-dim" title="avg.Listed — average asking price across active TTC listings (often inflated vs. actual sales).">
                avg.Listed: {gold(it.ttcAvgPrice)}
              </span>
            </Show>
            <Show when={!hasTTC}>
              <span class="v2-dim">no TTC data</span>
            </Show>
          </span>
        </div>
      </div>

      <div class="v2-pc-row__meta">
        <span><span class="v2-pc-row__meta-k">Last</span> {it.lastSeen ? relTime(it.lastSeen) : '—'}</span>
        <span><span class="v2-pc-row__meta-k">Freq</span> {freqLabel(it.sellFrequency)}</span>
        <Show when={hasEH}>
          <span class="v2-pc-row__eh-badge" title="ESO-Hub PC-NA server-wide confirmed sales average and sell-through ratio.">
            <span class="v2-pc-row__meta-k">ESO-Hub</span>
            {it.esoHubSaleAvg > 0 ? gold(it.esoHubSaleAvg) : gold(it.esoHubListAvg)}
            <Show when={it.esoHubSellThrough > 0}>
              <em class="v2-pc-row__eh-st"> ({Math.round(Math.min(1, it.esoHubSellThrough) * 100)}% ST)</em>
            </Show>
          </span>
        </Show>
        <Show when={it.level}>
          <span><span class="v2-pc-row__meta-k">Level</span> {it.level}</span>
        </Show>
        <Show when={(it.guilds || []).length}>
          <span class="v2-pc-row__guilds" title={(it.guilds || []).join(', ')}>
            {(it.guilds || []).slice(0, 2).join(', ')}{(it.guilds || []).length > 2 ? '…' : ''}
          </span>
        </Show>
      </div>
      <FissalSuggestionRow name={it.name} />
    </div>
  );
}

/**
 * Issue #114 — Fissal pricing suggestion line. Uses IntersectionObserver
 * and an AbortController so only rows scrolled into view trigger
 * `/api/items/:name/fissal`, eliminating 50-request HTTP bursts on search.
 */
function FissalSuggestionRow(props) {
  const key = (props.name || '').toLowerCase();
  const cached = FISSAL_CACHE.get(key);
  const [state, setState] = createSignal(cached
    ? { status: 'ready', data: cached }
    : { status: 'idle' });
  let anchorRef;

  onMount(() => {
    if (cached) return;
    const name = props.name;
    if (!name) { setState({ status: 'error' }); return; }

    let controller = null;
    let observer = null;
    let started = false;

    const triggerFetch = async () => {
      if (started) return;
      started = true;
      if (observer) { observer.disconnect(); observer = null; }
      const hit = FISSAL_CACHE.get(key);
      if (hit) {
        setState({ status: 'ready', data: hit });
        return;
      }
      setState({ status: 'loading' });
      controller = new AbortController();
      try {
        const r = await fetch('/api/items/' + encodeURIComponent(name) + '/fissal', {
          credentials: 'same-origin',
          signal: controller.signal,
        });
        if (!r.ok) { setState({ status: 'error' }); return; }
        const j = await r.json();
        if (FISSAL_CACHE.size > 500) {
          const oldest = FISSAL_CACHE.keys().next().value;
          if (oldest !== undefined) FISSAL_CACHE.delete(oldest);
        }
        FISSAL_CACHE.set(key, j);
        setState({ status: 'ready', data: j });
      } catch (err) {
        if (err?.name === 'AbortError') return;
        setState({ status: 'error' });
      }
    };

    if (typeof IntersectionObserver !== 'undefined' && anchorRef) {
      observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) triggerFetch();
      }, { rootMargin: '80px' });
      observer.observe(anchorRef);
    } else {
      triggerFetch();
    }

    onCleanup(() => {
      if (observer) observer.disconnect();
      if (controller) controller.abort();
    });
  });

  const data = () => state().data || null;
  const suggested = () => data()?.fissalSuggested;

  return (
    <div ref={el => anchorRef = el}>
      <Show when={state().status !== 'error' && (state().status === 'loading' || suggested() != null)}>
        <div class="v2-pc-row__fissal" title={data()?.reasoning?.text || ''}>
          <span class="v2-pc-row__fissal-tag">🐱 Fissal</span>
          <Show when={state().status === 'loading'}
                fallback={
                  <>
                    <span class="v2-pc-row__fissal-value">{gold(suggested())}</span>
                    <Show when={data()?.quickSell}>
                      <span class="v2-pc-row__fissal-tier v2-pc-row__fissal-tier--quick" title="Quick Sell price band">
                        ⚡ {gold(data().quickSell)}
                      </span>
                    </Show>
                    <Show when={data()?.patientList}>
                      <span class="v2-pc-row__fissal-tier v2-pc-row__fissal-tier--patient" title="Patient List price band">
                        ⏳ {gold(data().patientList)}
                      </span>
                    </Show>
                    <Show when={data()?.source}>
                      <span class="v2-pc-row__fissal-src">{formatFissalSource(data().source)}</span>
                    </Show>
                    <Show when={data()?.confidence && data()?.confidence !== 'none'}>
                      <span class="v2-pc-row__fissal-conf">{data().confidence}</span>
                    </Show>
                  </>
                }>
            <span class="v2-pc-row__fissal-loading">computing…</span>
          </Show>
        </div>
      </Show>
    </div>
  );
}
